/**
 * ThemeProvider / useTheme — resolves `@carn/tokens` for the DOM.
 *
 * CLAUDE.md: "Components read the theme from `useTheme()` and copy from `useCopy()`.
 * Never import raw palette ramps into a screen; never hardcode a colour, size, or
 * duration." `packages/ui`'s `ThemeProvider` implements the same contract for React
 * Native (`useColorScheme`, `AccessibilityInfo`), which this app does not depend on
 * (out of scope per this task's brief, and `packages/ui` is being edited by another
 * agent concurrently) — this file resolves the identical `@carn/tokens.resolveTheme()`
 * against the DOM's own signals (`matchMedia`) instead, so `apps/web` gets the same
 * theme *values* through a platform-appropriate reader.
 *
 * Scope is deliberately small next to the mobile provider: this app has no persisted
 * per-user settings store (no SQLite, no account) and no per-channel Intensity mixer
 * UI, so it exposes exactly what apps/web's two surfaces need — the resolved theme,
 * the live colour scheme, and the literal/warm register toggle (product-concept §11).
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_INTENSITY,
  DEFAULT_READING,
  channelLevel,
  resolveTheme,
  type ColourScheme,
  type Theme,
} from '@carn/tokens';
import { DEFAULT_INTENSITY_PREFERENCES } from '@carn/core';

/**
 * Plain words, on, unless this browser has said otherwise (docs/DEVIATIONS.md D30).
 *
 * DERIVED from the shipped app default rather than restated: the app expresses "wording defaults
 * to plain" as a `voice` pin on `DEFAULT_INTENSITY_PREFERENCES`, and a second `true` written here
 * would be a copy of that decision able to drift from it. Reverting the default in `@carn/core`
 * reverts this page in the same commit.
 */
const DEFAULT_PLAIN_WORDS_ON_WEB = channelLevel(DEFAULT_INTENSITY_PREFERENCES, 'voice') === 0;

type ThemeContextValue = {
  theme: Theme;
  literal: boolean;
  setLiteral: (literal: boolean) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** `prefers-color-scheme`, live. Defaults to light if the media query is unsupported. */
function useSystemScheme(): ColourScheme {
  const [scheme, setScheme] = useState<ColourScheme>(() =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light',
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setScheme(e.matches ? 'dark' : 'light');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return scheme;
}

/** `prefers-reduced-motion`, live — the OS switch outranks everything (design-direction §5). */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const scheme = useSystemScheme();
  const reduceMotion = usePrefersReducedMotion();
  /**
   * Literal mode persists locally, same idea as the mobile app's settings store, but
   * there is no account here — this is a per-browser preference (product-concept §11).
   *
   * DEFAULTS ON, matching the app a reader of this page would install (docs/DEVIATIONS.md D30).
   * The stored value is read for what it says rather than for equality with `'1'`, so a
   * browser that has explicitly turned plain words OFF keeps the warm register; only a browser
   * that has never touched the toggle takes the default.
   *
   * The mechanism differs from the app's on purpose: there is no mixer on a marketing page, so
   * this surface resolves its theme from `DEFAULT_INTENSITY` (dial default, `voice` unpinned)
   * and drives the register with this boolean alone. Pinning `voice` here instead would make
   * `LiteralToggle` inert in one direction.
   */
  const [literal, setLiteral] = useState<boolean>(() => {
    if (typeof window === 'undefined') return DEFAULT_PLAIN_WORDS_ON_WEB;
    const stored = window.localStorage.getItem('carn.web.literal');
    return stored === null ? DEFAULT_PLAIN_WORDS_ON_WEB : stored === '1';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('carn.web.literal', literal ? '1' : '0');
  }, [literal]);

  const theme = useMemo(
    () =>
      resolveTheme({
        // The dial's midpoint (`DEFAULT_INTENSITY`), same default the app ships —
        // this is a public web page, not a settings surface, so there is no mixer.
        intensity: DEFAULT_INTENSITY,
        scheme,
        density: 'sparse',
        reading: DEFAULT_READING,
        system: { reduceMotion },
        literal,
      }),
    [scheme, reduceMotion, literal],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, literal, setLiteral }),
    [theme, literal],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): Theme {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error(
      'useTheme() outside a ThemeProvider. Every Carn surface reads colour, size and ' +
        'duration from the theme (CLAUDE.md: never hardcode one), so there is no default.',
    );
  }
  return ctx.theme;
}

/** The literal/warm register toggle (product-concept §11's literal-language mode). */
export function useLiteralToggle(): [boolean, (literal: boolean) => void] {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useLiteralToggle() outside a ThemeProvider.');
  return [ctx.literal, ctx.setLiteral];
}
